import { useState } from 'react'
import { Box, TextField } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import { useTableStore } from '@/store/table/table'
import FilterCard from './FilterCard'
import { numberCodec, type NumberValue } from './filterCodecs'
import type { FilterWidgetProps } from './filterKinds'

const parse = (text: string): number | undefined => {
  const trimmed = text.trim()
  if (trimmed === '') return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

/** Keeps digits, one decimal point, and a minus in the first place. */
const numbersOnly = (text: string): string => {
  let seenPoint = false
  let out = ''
  for (const char of text) {
    if (char >= '0' && char <= '9') out += char
    else if (char === '-' && out === '') out += char
    else if (char === '.' && !seenPoint) {
      seenPoint = true
      out += char
    }
  }
  return out
}

const NumberRangeFilter = ({ field }: FilterWidgetProps) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { apiFilters, setApiFilters } = useTableStore()
  const stored = numberCodec.fromApi(apiFilters.filter(f => f.name === field))
  const [min, setMin] = useState(stored?.min !== undefined ? String(stored.min) : '')
  const [max, setMax] = useState(stored?.max !== undefined ? String(stored.max) : '')

  const value: NumberValue = { min: parse(min), max: parse(max) }
  const reversed =
    value.min !== undefined && value.max !== undefined && value.min > value.max
  const empty = value.min === undefined && value.max === undefined
  const isStored = stored !== null

  const apply = () => {
    const others = apiFilters.filter(f => f.name !== field)
    setApiFilters([...others, ...numberCodec.toApi(field, value)])
  }

  const clear = () => {
    setMin('')
    setMax('')
    setApiFilters(apiFilters.filter(f => f.name !== field))
  }

  const inputSx = {
    backgroundColor: isDarkMode ? '#0D192F' : '#F8FAFD',
    '& .MuiInputBase-input': { color: isDarkMode ? '#fff' : '#003F61' },
    '& .MuiFormLabel-root': { color: isDarkMode ? '#8F98A5' : '#003F61' },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? '#445876' : 'rgba(0,0,0,0.23)',
    },
  }

  return (
    <FilterCard
      title="טווח מספרים"
      isActive={isStored || !empty}
      onClear={clear}
      apply={{ disabled: empty || reversed, onApply: apply }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
          label="מ-"
          value={min}
          onChange={e => setMin(numbersOnly(e.target.value))}
          error={reversed}
          sx={inputSx}
        />
        <TextField
          size="small"
          inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
          label="עד"
          value={max}
          onChange={e => setMax(numbersOnly(e.target.value))}
          error={reversed}
          helperText={reversed ? 'הערך הנמוך גדול מהגבוה' : undefined}
          sx={inputSx}
        />
      </Box>
    </FilterCard>
  )
}

export default NumberRangeFilter
